import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Camera } from "lucide-react";
import { useCamera } from "../../hooks/useCamera";
import { FaceCapture } from "../attendance/FaceCapture";
import { enrollFace } from "../../api/directory";

const READY = { allowed: true, checks: [] };

export const FaceEnrollButton = ({ userId, enrolled, onDone, guard, className }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [phase, setPhase] = useState("idle");
  const camera = useCamera(open);

  const openNow = () => { setConfirm(false); setPhase("idle"); setOpen(true); };
  const start = () => (enrolled ? setConfirm(true) : openNow());

  const capture = async () => {
    setPhase("verifying");
    try {
      const [frame] = await camera.grabFrames(1);
      await enrollFace(userId, frame);
      setPhase("success");
      toast.success(t("detail.faceSaved"));
      setOpen(false);
      onDone();
    } catch (e) {
      setPhase("error");
      const code = e?.response?.data?.detail?.code;
      toast.error(code ? t(`errors.${code}`, t("detail.faceFailed")) : t("detail.faceFailed"));
    }
  };

  const label = t(enrolled ? "detail.retakeFace" : "detail.enrollFace");
  return (
    <>
      <button type="button" data-testid="detail-enroll-face" className={className}
        aria-label={label} title={label} onClick={() => (guard ? guard(start) : start())}>
        <Camera className="h-4 w-4" />
      </button>
      {confirm ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="face-confirm">
          <div className="w-full max-w-sm rounded-3xl bg-background p-6 text-center shadow-neu">
            <p className="text-base font-bold">{t("detail.faceUpdateConfirm")}</p>
            <div className="mt-5 flex justify-center gap-3">
              <button data-testid="face-confirm-cancel" onClick={() => setConfirm(false)}
                className="rounded-2xl bg-background px-5 py-2.5 text-sm font-bold shadow-neu-sm">
                {t("detail.faceUpdateNo")}
              </button>
              <button data-testid="face-confirm-ok" onClick={openNow}
                className="rounded-2xl bg-primary px-5 py-2.5 text-sm font-bold text-white shadow-neu-sm">
                {t("detail.faceUpdateYes")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      <FaceCapture open={open} phase={phase} message={t("detail.faceHint")}
        actionLabel={t("detail.faceShoot")} camera={camera} readiness={READY}
        checking={false} onClose={() => setOpen(false)} onCapture={capture} />
    </>
  );
};
